import { useEffect, useRef, useMemo } from "react";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import {
  CompanyInstallsPayload,
  COMPANY_INSTALL_LOCATIONS_BY_PRODUCTS_AND_COUNTRY_QUERY,
} from "../../../../../../legacy/queries/Installs";
import { useQuery } from "@apollo/client";
import { InstallSectionCard } from "../InstallSectionCard";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_worldLow from "@amcharts/amcharts4-geodata/worldLow";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";

interface MapDataItem {
  id: string;
  name: string;
  products: number;
  locations: number;
  value: number;
}

export const InstallsByCountry = () => {
  const company = useCompanyPageStore((state) => state.company);
  const installFilters = useCompanyPageStore((state) => state.installFilters);
  const mapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<am4maps.MapChart | null>(null);

  const {
    data: companyInstallsData,
    loading: companyInstallsLoading,
    error: companyInstallsError,
  } = useQuery<CompanyInstallsPayload>(
    COMPANY_INSTALL_LOCATIONS_BY_PRODUCTS_AND_COUNTRY_QUERY,
    {
      variables: {
        hgId: company!.hgId,
      },
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
      skip: !company?.hgId,
    }
  );

  const {
    data: installsData,
    loading: installsLoading,
    error: installsError,
  } = useQuery<CompanyInstallsPayload>(
    COMPANY_INSTALL_LOCATIONS_BY_PRODUCTS_AND_COUNTRY_QUERY,
    {
      variables: {
        hgId: company!.hgId,
        countryAlpha2s: installFilters.countryAlpha2s,
        productIds: installFilters.productIds,
        vendorIds: installFilters.vendorIds,
        productCategoryIds: installFilters.productCategoryIds,
        productAttributeIds: installFilters.productAttributeIds,
      },
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
      skip: !company?.hgId,
    }
  );

  const mapData = useMemo(() => {
    if (!installsData?.install?.data || !companyInstallsData?.install?.data) {
      return [];
    }

    // Filter out items with missing data and get max products value for bubble sizing
    const validCompanyData = companyInstallsData.install.data.filter(
      (item) => item.products !== undefined && item.products !== null
    );

    if (validCompanyData.length === 0) {
      return [];
    }

    const maxProducts = Math.max(
      ...validCompanyData.map((item) => item.products!)
    );

    return installsData.install.data
      .filter(
        (item) =>
          item.location?.state?.country?.alpha2 &&
          item.location?.state?.country?.name &&
          item.products !== undefined &&
          item.locations !== undefined
      )
      .map((item) => ({
        id: item.location!.state!.country!.alpha2,
        name: item.location!.state!.country!.name,
        products: item.products!,
        locations: item.locations!,
        // Use products count for bubble sizing
        value: (item.products! / maxProducts) * 100,
      }));
  }, [installsData, companyInstallsData]);

  // Effect for chart creation (runs once when mapRef is available)
  useEffect(() => {
    if (!mapRef.current || chartRef.current) {
      return;
    }

    const chart = am4core.create(mapRef.current, am4maps.MapChart);
    chartRef.current = chart;

    // Set map definition
    chart.geodata = am4geodata_worldLow;

    // Set projection
    chart.projection = new am4maps.projections.Miller();

    // Create map polygon series
    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.exclude = ["AQ"];
    polygonSeries.useGeodata = true;
    polygonSeries.nonScalingStroke = true;
    polygonSeries.strokeWidth = 0.5;
    polygonSeries.calculateVisualCenter = true;
    polygonSeries.mapPolygons.template.fill = am4core.color("#E8E8E8");
    polygonSeries.mapPolygons.template.stroke = am4core.color("#FFFFFF");

    // Invalidate image series when polygon series is validated
    polygonSeries.events.on("validated", function () {
      imageSeries.invalidate();
    });

    // Create image series for bubbles
    const imageSeries = chart.series.push(new am4maps.MapImageSeries());
    imageSeries.data = []; // Start empty, will be populated by data update effect
    imageSeries.dataFields.value = "value";

    const imageTemplate = imageSeries.mapImages.template;
    imageTemplate.nonScaling = true;

    // Use adapters to position bubbles on country centers
    imageTemplate.adapter.add("latitude", function (latitude, target) {
      const polygon = polygonSeries.getPolygonById(
        (target.dataItem.dataContext as MapDataItem).id
      );
      if (polygon) {
        return polygon.visualLatitude;
      }
      return latitude;
    });

    imageTemplate.adapter.add("longitude", function (longitude, target) {
      const polygon = polygonSeries.getPolygonById(
        (target.dataItem.dataContext as MapDataItem).id
      );
      if (polygon) {
        return polygon.visualLongitude;
      }
      return longitude;
    });

    // Create circle for bubbles
    const circle = imageTemplate.createChild(am4core.Circle);
    circle.fillOpacity = 0.8;
    circle.fill = am4core.color("#4285F4");
    circle.stroke = am4core.color("#FFFFFF");
    circle.strokeWidth = 2;

    // Custom tooltip formatter for installs data
    circle.adapter.add("tooltipText", function (_text, target) {
      if (target.dataItem && target.dataItem.dataContext) {
        const dataContext = target.dataItem.dataContext as MapDataItem;
        const countryName = dataContext.name.includes("(the)")
          ? dataContext.name.replace(" (the)", "")
          : dataContext.name;

        return `[bold]${countryName}[/]\n[bold]${dataContext.products}[/] products installed\n[bold]${dataContext.locations.toLocaleString()}[/] locations`;
      }
    });

    // Set up heat rules for bubble sizing
    imageSeries.heatRules.push({
      target: circle,
      property: "radius",
      min: 4,
      max: 30,
      dataField: "value",
    });

    // Add hover effects
    const hoverState = circle.states.create("hover");
    hoverState.properties.scale = 1.2;
    hoverState.properties.fill = am4core.color("#3367D6");

    // Add small map
    chart.smallMap = new am4maps.SmallMap();
    chart.smallMap.series.push(polygonSeries);

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  // Effect for data updates (runs when mapData changes)
  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const imageSeries = chartRef.current.series.getIndex(
      1
    ) as am4maps.MapImageSeries;
    if (imageSeries) {
      imageSeries.data = mapData;

      // Update heat rule based on data length
      if (imageSeries.heatRules.length > 0) {
        const heatRule = imageSeries.heatRules.getIndex(0);
        if (heatRule) {
          heatRule.min = mapData.length === 1 ? 30 : 4;
        }
      }
    }
  }, [mapData]);

  const loading = companyInstallsLoading || installsLoading;
  const error = companyInstallsError || installsError;

  const showLoading = loading || !company;
  const showError = !showLoading && error;
  const showMap = !showLoading && !showError;
  const showNoData = showMap && mapData.length === 0;

  return (
    <InstallSectionCard
      title="Installs By Country"
      filterKey="INSTALLS_BY_COUNTRY"
    >
      {/* Always render the map div to preserve ref */}
      <div
        ref={mapRef}
        style={{
          width: "90%",
          height: 350,
          display: showMap && mapData.length > 0 ? "block" : "none",
        }}
      />

      {showLoading && (
        <Skeleton variant="rectangular" width="100%" height={350} />
      )}

      {showError && (
        <Stack
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={350}
        >
          <Typography color="error">Error loading data.</Typography>
        </Stack>
      )}

      {showNoData && (
        <Stack
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={350}
        >
          <Typography variant="body1" color="text.secondary">
            No install data available for the current selection.
          </Typography>
        </Stack>
      )}
    </InstallSectionCard>
  );
};
